import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { StudioPage, Metric, Badge, DataTable, Drawer, DBlock, DKV, Panel, Loading, Empty, cx, confColor } from "./StudioShared";
import {
  DollarSign, Cpu, Zap, Target, Clock, Gauge, CheckCircle2, TrendingUp,
  UserCheck, PenLine, Server, ArrowDownToLine,
} from "lucide-react";

const PRESETS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["year", "This Year"]];
const usd = (n) => (n == null ? "—" : `$${Number(n).toFixed(n < 1 ? 4 : 2)}`);
const fmtMs = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);
const fmtTime = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");

// Canonical lifecycle order + icons for the Mission Timeline replay.
const STEP_ICON = {
  customer_request: Target, mission_created: Zap, strategist_selected: UserCheck,
  knowledge_retrieved: ArrowDownToLine, workflow_started: TrendingUp, provider_selected: Server,
  model_selected: Cpu, generation_completed: PenLine, quality_review: Gauge,
  human_reviewed: UserCheck, human_edited: PenLine, human_approved: CheckCircle2,
  saved_to_project: ArrowDownToLine, saved_to_client: ArrowDownToLine, routed: TrendingUp,
  exported: ArrowDownToLine, published: CheckCircle2, archived: Clock, completed: CheckCircle2, failed: Clock,
};

export default function AIIntelligence() {
  const [preset, setPreset] = useState("month");
  const [sum, setSum] = useState(null);
  const [missions, setMissions] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setSum(null); setMissions(null);
    try {
      const [s, m] = await Promise.all([
        api.get(`/ai/intelligence/summary?preset=${preset}`),
        api.get(`/ai/intelligence/missions?limit=100`),
      ]);
      setSum(s.data); setMissions(m.data.missions || []);
    } catch {
      setSum({}); setMissions([]);
    }
  }, [preset]);
  useEffect(() => { load(); }, [load]);

  const cost = sum?.cost || {};
  const hl = sum?.highlights || {};

  return (
    <StudioPage
      title="AI Intelligence Center"
      question="What did AIPOS do — how, why, how long did it take, and what did it cost?"
      testid="ai-intelligence-center"
      actions={
        <select value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="ai-intel-preset"
          className="bg-navy-800 border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      }
    >
      {sum === null ? <Loading label="Loading mission telemetry…" /> : (
        <div className="space-y-6">
          {/* Executive telemetry */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ai-intel-metrics">
            <Metric label="Missions" value={sum.total_missions ?? 0} icon={Target} testid="metric-missions" />
            <Metric label="Effective Cost" value={usd(cost.effective)} sub={cost.variance ? `var ${usd(cost.variance)}` : "estimated"} icon={DollarSign} testid="metric-cost" />
            <Metric label="Est. Cost" value={usd(cost.estimated)} icon={DollarSign} testid="metric-cost-est" />
            <Metric label="Actual Cost" value={cost.actual ? usd(cost.actual) : "—"} sub={cost.actual ? "billed" : "awaiting provider"} icon={DollarSign} testid="metric-cost-actual" />
            <Metric label="Cost / Mission" value={usd(cost.per_mission)} icon={DollarSign} testid="metric-cost-per-mission" />
            <Metric label="Avg Tokens" value={sum.tokens?.avg_per_mission ?? 0} icon={Cpu} testid="metric-tokens" />
            <Metric label="Success Rate" value={sum.success_rate != null ? `${Math.round(sum.success_rate * 100)}%` : "—"} icon={CheckCircle2} testid="metric-success" />
            <Metric label="Approval Rate" value={sum.approval_rate != null ? `${Math.round(sum.approval_rate * 100)}%` : "—"} icon={UserCheck} testid="metric-approval" />
            <Metric label="Most Used Provider" value={sum.most_used_provider || "—"} icon={Server} testid="metric-provider" />
            <Metric label="Most Used Model" value={sum.most_used_model || "—"} icon={Cpu} testid="metric-model" />
            <Metric label="Cost / Client" value={usd(cost.per_client)} icon={DollarSign} testid="metric-cost-client" />
            <Metric label="Cost / Project" value={usd(cost.per_project)} icon={DollarSign} testid="metric-cost-project" />
          </div>

          {/* Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ai-intel-highlights">
            <Highlight title="Most Expensive Mission" m={hl.most_expensive} kind="cost" />
            <Highlight title="Cheapest Mission" m={hl.cheapest} kind="cost" />
            <Highlight title="Highest Confidence" m={hl.highest_confidence} kind="conf" />
            <Highlight title="Longest Mission" m={hl.longest} kind="dur" />
            <Highlight title="Fastest Mission" m={hl.fastest} kind="dur" />
            <Panel className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Provider Usage</div>
              <div className="mt-2 space-y-1">
                {(sum.provider_usage || []).length === 0 ? <div className="text-slate-500 text-xs">No data yet.</div> :
                  (sum.provider_usage || []).map((p) => (
                    <div key={p.provider} className="flex items-center justify-between text-xs">
                      <span className="text-slate-300">{p.provider}</span><Badge tone="slate">{p.count}</Badge>
                    </div>
                  ))}
              </div>
            </Panel>
          </div>

          {/* Mission log */}
          <div>
            <div className="text-white text-sm font-semibold mb-2">Mission Log</div>
            {missions === null ? <Loading /> : (
              <DataTable
                testid="ai-intel-missions-table"
                empty="No missions recorded yet. Generate content in the AI Content Studio to populate the telemetry."
                columns={[
                  { key: "name", label: "Mission", render: (r) => <span className="text-white">{r.name}</span> },
                  { key: "provider", label: "Provider / Model", render: (r) => <span className="text-slate-300">{r.provider || "—"}<span className="text-slate-600"> · {r.model_id || "—"}</span></span> },
                  { key: "cost", label: "Cost", render: (r) => <span className="text-gold">{usd(r.cost_actual != null ? r.cost_actual : r.cost_estimated)}<span className="text-[10px] text-slate-600"> {r.cost_source || ""}</span></span> },
                  { key: "total_duration_ms", label: "Duration", render: (r) => <span className="text-slate-300">{fmtMs(r.total_duration_ms)}</span> },
                  { key: "confidence_score", label: "Confidence", render: (r) => <span className={confColor(r.confidence_score || 0)}>{r.confidence_score != null ? `${Math.round(r.confidence_score * 100)}%` : "—"}</span> },
                  { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "completed" ? "emerald" : r.status === "failed" ? "red" : "slate"}>{r.status}</Badge> },
                ]}
                rows={missions}
                rowTestid={(r) => `mission-row-${r.id}`}
                onRowClick={(r) => setOpenId(r.id)}
              />
            )}
          </div>
        </div>
      )}

      <MissionTimelineDrawer openId={openId} onClose={() => setOpenId(null)} />
    </StudioPage>
  );
}

function Highlight({ title, m, kind }) {
  return (
    <Panel className="p-3" testid={`highlight-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{title}</div>
      {!m ? <div className="text-slate-500 text-xs mt-1">No data yet.</div> : (
        <div className="mt-1">
          <div className="text-white text-sm truncate">{m.name}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            {kind === "cost" && <span className="text-gold">{usd(m.cost)}</span>}
            {kind === "dur" && <span>{fmtMs(m.duration_ms)}</span>}
            {kind === "conf" && <span className={confColor(m.confidence || 0)}>{m.confidence != null ? `${Math.round(m.confidence * 100)}%` : "—"}</span>}
            <span className="text-slate-600"> · {m.model_id || m.provider || "—"}</span>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function MissionTimelineDrawer({ openId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!openId) { setData(null); return; }
    setData(null);
    api.get(`/ai/intelligence/missions/${openId}/timeline`).then((r) => setData(r.data)).catch(() => setData({ mission: null, steps: [] }));
  }, [openId]);

  const m = data?.mission;
  const steps = data?.steps || [];

  return (
    <Drawer open={!!openId} onClose={onClose} title="Mission Timeline"
      subtitle={m ? m.name : "Loading…"} testid="mission-timeline-drawer">
      {!data ? <Loading label="Loading mission timeline…" /> : !m ? <Empty text="Mission not found." testid="mission-timeline-empty" /> : (
        <div className="space-y-5">
          {/* Mission header telemetry */}
          <DBlock title="Mission">
            <div className="grid grid-cols-2 gap-2">
              <DKV label="Status" value={m.status} />
              <DKV label="Source" value={m.source} />
              <DKV label="Provider" value={m.provider} />
              <DKV label="Model" value={m.model} />
              <DKV label="OpenRouter Route" value={m.openrouter_route} />
              <DKV label="Total Duration" value={fmtMs(m.total_duration_ms)} />
              <DKV label="Total Tokens" value={m.total_tokens} />
              <DKV label="Prompt / Completion" value={m.prompt_tokens != null ? `${m.prompt_tokens} / ${m.completion_tokens}` : null} />
              <DKV label="Created By" value={m.created_by} />
              <DKV label="Started" value={fmtTime(m.start_time)} />
            </div>
          </DBlock>

          {/* Hybrid cost */}
          <DBlock title="Cost (Hybrid)">
            <div className="grid grid-cols-3 gap-2">
              <div className="border border-white/10 rounded-sm p-2"><div className="text-[10px] uppercase text-slate-500">Estimated</div><div className="text-gold text-sm">{usd(m.cost_estimated)}</div></div>
              <div className="border border-white/10 rounded-sm p-2"><div className="text-[10px] uppercase text-slate-500">Actual</div><div className="text-slate-200 text-sm">{m.cost_actual != null ? usd(m.cost_actual) : "—"}</div></div>
              <div className="border border-white/10 rounded-sm p-2"><div className="text-[10px] uppercase text-slate-500">Displaying</div><div className="text-slate-200 text-sm capitalize">{m.cost_source || "estimated"}</div></div>
            </div>
          </DBlock>

          {/* Quality + human lifecycle */}
          <DBlock title="Quality & Human Review">
            <div className="grid grid-cols-2 gap-2">
              <DKV label="Confidence" value={m.confidence_score != null ? `${Math.round(m.confidence_score * 100)}%` : null} />
              <DKV label="Confidence Source" value={m.confidence_source} />
              <DKV label="Retries" value={m.retry_count} />
              <DKV label="Failover" value={m.failover_triggered ? "Yes" : "No"} />
              <DKV label="Revisions" value={m.revisions} />
              <DKV label="Human Edited" value={m.human_edited ? "Yes" : "No"} />
              <DKV label="Reviewed By" value={m.reviewed_by} />
              <DKV label="Approved By" value={m.approved_by} />
              <DKV label="Published By" value={m.published_by} />
            </div>
          </DBlock>

          {/* Lifecycle replay */}
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Lifecycle Replay</div>
            <ol className="relative border-l border-white/10 ml-2" data-testid="mission-timeline-steps">
              {steps.length === 0 ? <div className="text-slate-500 text-xs ml-4">No steps recorded.</div> :
                steps.map((s, i) => {
                  const Icon = STEP_ICON[s.step] || Clock;
                  return (
                    <li key={i} className="mb-3 ml-4" data-testid={`mission-step-${s.step}`}>
                      <span className="absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full bg-navy border border-gold/40">
                        <span className={cx("w-1.5 h-1.5 rounded-full", s.status === "failed" ? "bg-red-400" : "bg-emerald-400")} />
                      </span>
                      <div className="flex items-start gap-2">
                        <Icon size={14} className="text-gold mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm">{s.label}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                            <span>{fmtTime(s.timestamp)}</span>
                            {s.duration_ms != null && <span>· {fmtMs(s.duration_ms)}</span>}
                            {s.cost != null && <span>· <span className="text-gold">{usd(s.cost)}</span></span>}
                            {s.model && <span>· {s.model}</span>}
                            {s.confidence != null && <span>· <span className={confColor(s.confidence)}>{Math.round(s.confidence * 100)}%</span></span>}
                            {s.note && <span>· {s.note}</span>}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ol>
          </div>
        </div>
      )}
    </Drawer>
  );
}
